import React, { useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { CompanyScopeProvider } from './context/CompanyScopeContext';
import { Toaster } from './components/ui/sonner';
import MainLayout from './components/MainLayout';
import Login from './pages/Login';
import ChangePassword from './pages/ChangePassword';
import Dashboard from './pages/Dashboard';
import Companies from './pages/Companies';
import Employees from './pages/Employees';
import EmployeeProfile from './pages/EmployeeProfile';
import Departments from './pages/Departments';
import Attendance from './pages/Attendance';
import HRSettings from './pages/HRSettings';
import Payroll from './pages/Payroll';
import Reports from './pages/Reports';
import Profile from './pages/Profile';
import Leaves from './pages/Leaves';
import UsersManagement from './pages/UsersManagement';
import EmployeeDashboard from './pages/EmployeeDashboard';
import MyAttendances from './pages/MyAttendances';
import MyLeaves from './pages/MyLeaves';
import MyPayslips from './pages/MyPayslips';
import JustificationsReview from './pages/JustificationsReview';
import SecuritySettings from './pages/SecuritySettings';
import AuditLogs from './pages/AuditLogs';
import QRAttendance from './pages/QRAttendance';
import Analytics from './pages/Analytics';
import PendingApprovals from './pages/PendingApprovals';
import CompanyEmployees from './pages/CompanyEmployees';
import ScheduleExceptions from './pages/ScheduleExceptions';
import MyScheduleExceptions from './pages/MyScheduleExceptions';
import MyShift from './pages/MyShift';
import ZKTecoDevices from './pages/ZKTecoDevices';
import Alerts from './pages/Alerts';
import ShiftManagement from './pages/ShiftManagement';
import LeaveTypes from './pages/LeaveTypes';
import PublicHolidays from './pages/PublicHolidays';
import OvertimeManagement from './pages/OvertimeManagement';
import LegalDeductions from './pages/LegalDeductions';
import PayrollAdmin from './pages/PayrollAdmin';
import MySalaryRequests from './pages/MySalaryRequests';
import SmtpSettings from './pages/SmtpSettings';
import PayrollSimulator from './pages/PayrollSimulator';
import EmailBroadcast from './pages/EmailBroadcast';
import './App.css';

const PAGE_TITLES = {
  '/login': 'Connexion',
  '/change-password': 'Changer le mot de passe',
  '/dashboard': 'Tableau de bord',
  '/companies': 'Entreprises',
  '/employees': 'Employés',
  '/departments': 'Départements',
  '/attendance': 'Présences',
  '/leaves': 'Congés & Absences',
  '/hr-settings': 'Paramètres RH',
  '/payroll': 'Gestion de la Paie',
  '/reports': 'Rapports & Statistiques',
  '/users': 'Gestion des Utilisateurs',
  '/justifications': 'Justificatifs d\'absence',
  '/smtp-settings': 'Paramètres Email SMTP',
  '/payroll/simulate': 'Simulateur de Paie',
  '/security': 'Sécurité & 2FA',
  '/audit-logs': 'Journaux d\'Audit',
  '/qr-attendance': 'Pointage QR Code',
  '/analytics': 'Analyses & Prédictions',
  '/approvals': 'Approbations',
  '/schedule-exceptions': 'Exceptions d\'Horaire',
  '/zkteco': 'Appareils Biométriques',
  '/alerts': 'Alertes RH',
  '/shifts': 'Gestion des Quarts',
  '/leave-types': 'Types de Congés',
  '/public-holidays': 'Jours Fériés',
  '/overtime': 'Heures Supplémentaires',
  '/legal-deductions': 'Cotisations Légales',
  '/email-broadcast': 'Emails groupés',
  '/profile': 'Mon Profil',
  '/employee/dashboard': 'Mon Espace Employé',
  '/employee/attendances': 'Mes Pointages',
  '/employee/leaves': 'Mes Demandes de Congés',
  '/employee/payslips': 'Mes Fiches de Paie',
  '/employee/schedule-exceptions': 'Mes Exceptions d\'Horaire',
  '/employee/shift': 'Mon Quart de Travail',
  '/employee/salary-requests': 'Mes Demandes d\'Acompte',
  '/employee/qr': 'Mon Pointage QR Code'
};

const PageTitleUpdater = () => {
  const location = useLocation();

  useEffect(() => {
    let pageTitle = PAGE_TITLES[location.pathname];
    if (!pageTitle && location.pathname.startsWith('/employees/')) {
      pageTitle = 'Profil Employé';
    } else if (!pageTitle && location.pathname.includes('/employees')) {
      pageTitle = 'Employés Entreprise';
    }

    if (pageTitle) {
      document.title = `LuxosGroup RH - ${pageTitle}`;
    } else {
      document.title = 'LuxosGroup RH';
    }
  }, [location]);

  return null;
};

// Protected route that checks for password change requirement
const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, mustChangePassword, loading } = useAuth();
  
  if (loading) {
    return <div className="min-h-screen flex items-center justify-center">Chargement...</div>;
  }
  
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }
  
  if (mustChangePassword) {
    return <Navigate to="/change-password" replace />;
  }
  
  return children;
};

function App() {
  return (
    <AuthProvider>
      <CompanyScopeProvider>
      <BrowserRouter>
        <PageTitleUpdater />
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route path="/change-password" element={<ChangePassword />} />
          <Route path="/" element={
            <ProtectedRoute>
              <MainLayout />
            </ProtectedRoute>
          }>
            <Route index element={<Navigate to="/dashboard" replace />} />
            <Route path="dashboard" element={<Dashboard />} />
            <Route path="companies" element={<Companies />} />
            <Route path="employees" element={<Employees />} />
            <Route path="employees/:employeeId" element={<EmployeeProfile />} />
            <Route path="departments" element={<Departments />} />
            <Route path="attendance" element={<Attendance />} />
            <Route path="leaves" element={<Leaves />} />
            <Route path="hr-settings" element={<HRSettings />} />
            <Route path="payroll" element={<PayrollAdmin />} />
            <Route path="reports" element={<Reports />} />
            <Route path="users" element={<UsersManagement />} />
            <Route path="justifications" element={<JustificationsReview />} />
            <Route path="smtp-settings" element={<SmtpSettings />} />
            <Route path="payroll/simulate" element={<PayrollSimulator />} />
            <Route path="security" element={<SecuritySettings />} />
            <Route path="audit-logs" element={<AuditLogs />} />
            <Route path="qr-attendance" element={<QRAttendance />} />
            <Route path="analytics" element={<Analytics />} />
            <Route path="approvals" element={<PendingApprovals />} />
            <Route path="schedule-exceptions" element={<ScheduleExceptions />} />
            <Route path="zkteco" element={<ZKTecoDevices />} />
            <Route path="alerts" element={<Alerts />} />
            <Route path="shifts" element={<ShiftManagement />} />
            <Route path="leave-types" element={<LeaveTypes />} />
            <Route path="public-holidays" element={<PublicHolidays />} />
            <Route path="overtime" element={<OvertimeManagement />} />
            <Route path="legal-deductions" element={<LegalDeductions />} />
            <Route path="companies/:companyId/employees" element={<CompanyEmployees />} />
            <Route path="employee/dashboard" element={<EmployeeDashboard />} />
            <Route path="employee/attendances" element={<MyAttendances />} />
            <Route path="employee/leaves" element={<MyLeaves />} />
            <Route path="employee/payslips" element={<MyPayslips />} />
            <Route path="employee/schedule-exceptions" element={<MyScheduleExceptions />} />
            <Route path="employee/shift" element={<MyShift />} />
            <Route path="employee/salary-requests" element={<MySalaryRequests />} />
            <Route path="employee/qr" element={<QRAttendance />} />
            <Route path="profile" element={<Profile />} />
            <Route path="email-broadcast" element={<EmailBroadcast />} />
          </Route>
          <Route path="*" element={<Navigate to="/dashboard" replace />} />
        </Routes>
        <Toaster position="top-right" richColors />
      </BrowserRouter>
      </CompanyScopeProvider>
    </AuthProvider>
  );
}

export default App;
